import { Box, Chip, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { useCurrentTenantInfo } from "@/common/hooks/entity/tenant/useCurrentTenantInfo";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { TenantDto, TenantGetPaginatedDto } from "@/core/api/generated/v1";

import TenantModeChip from "./TenantModeChip";

export type TenantAutocompleteOption = BaseAutocompleteOption<TenantDto>;

export const getTenantOptionTitle = (data: TenantDto, currentTenant?: Nil<TenantDto>): string =>
  StringHelper.join(
    [data.name, currentTenant && currentTenant.id === data.id ? "(you)" : undefined],
    " ",
    {
      skipEmpty: true,
    },
  );

export const tenantToOption = (data: TenantDto): TenantAutocompleteOption => ({
  id: data.id!,
  title: `${data.name}`,
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface TenantAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<TenantDto> {
  searchFilters?: Partial<Omit<TenantGetPaginatedDto, "search" | "includeIds">>;
}

export default function TenantAutocomplete({
  searchFilters,

  isPreload = true,
  sx,
  ...otherProps
}: TenantAutocompleteProps) {
  const currentTenantInfo = useCurrentTenantInfo();

  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1, ...sx }}
      entityToOption={tenantToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.tenantsApi.apiV1TenantsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          tenantGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          tenantGetPaginatedDto: {
            ...params.tenantGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Company'
      placeholder='Search...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props} secondaryAction={<TenantModeChip mode={option.data?.mode} />}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='company' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}{" "}
                    {currentTenantInfo?.identifier === option.data?.identifier && (
                      <Chip size='extraSmall' variant='outlined' color='default' label='You' />
                    )}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>{option.data?.identifier}</Box>
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
